import type { AccreditationService } from "@/services/ports"
import type { AccreditationReceipt, AccreditationRequest } from "@/services/types"
import { HttpError } from "@/services/api/httpClient"

/* ─────────────────────────────────────────────────────────────────────────────
 * Envio da ficha de credenciamento.
 *
 * Por que este service esta fora de `services/api` e `services/static`:
 * aqueles dois sao as duas fontes de LEITURA do site, escolhidas juntas por
 * `VITE_DATA_SOURCE`. O credenciamento e ESCRITA, e tem prontidao propria — o
 * destino que recebe a ficha e a manda por e-mail pode existir muito antes ou
 * muito depois da API publica de conteudo. Amarrar os dois na mesma variavel
 * significaria ou publicar o site inteiro contra uma API que nao existe, ou
 * segurar o formulario esperando por ela.
 *
 * Entao: `VITE_ACCREDITATION_ENDPOINT` vazia => `unavailable`. A tela pergunta
 * antes de deixar preencher e oferece WhatsApp e e-mail. Nada de aceitar o
 * formulario e descartar em silencio — quem preencheu ficaria esperando um
 * retorno que nunca viria.
 *
 * O que o endpoint precisa devolver: 2xx com `{ protocol, receivedAt }`.
 * ────────────────────────────────────────────────────────────────────────── */

const TIMEOUT_MS = 15_000

export class AccreditationUnavailableError extends Error {
  constructor() {
    super("Envio de credenciamento sem destino configurado (VITE_ACCREDITATION_ENDPOINT).")
    this.name = "AccreditationUnavailableError"
  }
}

/** Sem destino: recusa na hora, com um erro que a tela sabe reconhecer. */
export function createUnavailableAccreditationService(): AccreditationService {
  return {
    isAvailable: () => false,
    submit: () => Promise.reject(new AccreditationUnavailableError()),
  }
}

export function createHttpAccreditationService(endpoint: string): AccreditationService {
  return {
    isAvailable: () => true,

    async submit(request: AccreditationRequest): Promise<AccreditationReceipt> {
      // Sem timeout, um destino pendurado deixa o botao girando para sempre e
      // o candidato reenvia a ficha — duas vezes a mesma solicitacao.
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(TIMEOUT_MS),
      })

      if (!response.ok) {
        throw new HttpError(response.status, endpoint, `POST do credenciamento respondeu ${response.status}.`)
      }

      return (await response.json()) as AccreditationReceipt
    },
  }
}

export function createAccreditationService(endpoint: string): AccreditationService {
  return endpoint ? createHttpAccreditationService(endpoint) : createUnavailableAccreditationService()
}
